import { DebtAgeBadge } from "@/components/DebtAgeBadge";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatDate, formatTRY } from "@/lib/api";
import { openWhatsApp } from "@/lib/whatsapp";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CustomerTimeline } from "@/components/customer/CustomerTimeline";
import { WalletDialog } from "@/components/wallet/WalletDialog";
import { OPP_STYLE } from "@/components/wallet/OppChips";
import { Phone, MessageCircle, Pencil, Car, Cake, StickyNote, Sparkles, WalletCards } from "lucide-react";

const Stat = ({ label, value, cls = "text-zinc-900", tid }) => (
  <div className="rounded-lg border border-zinc-200 bg-white px-3 py-2" data-testid={tid}>
    <p className="text-[10px] uppercase tracking-wider text-zinc-400">{label}</p>
    <p className={`font-heading text-base font-semibold tabular ${cls}`}>{value}</p>
  </div>
);

const Vehicles = ({ list }) => !list.length ? null : (
  <div data-testid="customer-vehicles">
    <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Araçlar</p>
    <div className="flex flex-wrap gap-2">
      {list.map((v) => (
        <div key={v.plate} className={`rounded-lg border px-3 py-2 ${v.active ? "border-zinc-300 bg-white" : "border-dashed border-zinc-200 bg-zinc-50 opacity-70"}`} data-testid={`vehicle-${v.plate}`}>
          <p className="inline-flex items-center gap-1.5 font-mono text-sm font-bold tracking-wider text-zinc-900"><Car className="h-3.5 w-3.5 text-zinc-400" />{v.plate}{v.model_year ? <span className="font-sans font-normal text-xs text-zinc-500">· {v.model_year}</span> : null}</p>
          <p className="text-[11px] text-zinc-500 mt-0.5">{v.types.length ? v.types.join(" · ") : "Aktif poliçe yok"}</p>
        </div>
      ))}
    </div>
  </div>
);

const Notes = ({ d }) => !(d.notes || d.policy_notes.length) ? null : (
  <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3 space-y-1.5" data-testid="customer-notes">
    <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800"><StickyNote className="h-3.5 w-3.5" />Notlar</p>
    {d.notes && <p className="text-sm text-amber-900 whitespace-pre-line">{d.notes}</p>}
    {d.policy_notes.map((n, i) => <p key={i} className="text-xs text-amber-900"><b>{n.label}:</b> {n.notes}</p>)}
  </div>
);

const Opportunities = ({ list }) => {
  const items = list.filter((o) => o.code !== "dogum");
  if (!items.length) return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3" data-testid="customer-opportunities">
      <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 mb-2"><Sparkles className="h-3.5 w-3.5" />Çapraz satış fırsatları</p>
      <ul className="grid sm:grid-cols-2 gap-2">
        {items.map((o) => {
          const S = OPP_STYLE[o.code] || {};
          const I = S.icon || Sparkles;
          return (
            <li key={o.code} className="flex items-start gap-2 rounded-md bg-white border border-amber-100 px-2.5 py-2" data-testid={`customer-opp-${o.code}`}>
              <I className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div><p className="text-[13px] font-medium text-zinc-900 leading-tight">{o.label}</p>{S.hint && <p className="text-[11px] text-zinc-500">{S.hint}</p>}</div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const CustomerCardHost = () => {
  const navigate = useNavigate();
  const [target, setTarget] = useState(null);
  const [d, setD] = useState(null);
  const [walletOpen, setWalletOpen] = useState(false);
  useEffect(() => {
    const on = (e) => { setD(null); setTarget(e.detail); };
    window.addEventListener("open-customer-card", on);
    return () => window.removeEventListener("open-customer-card", on);
  }, []);
  const load = useCallback(() => {
    if (!target) return;
    api.get("/customers/timeline", { params: { key: target.key || undefined, policy_id: target.policyId || undefined } })
      .then((r) => setD(r.data)).catch(() => setD(false));
  }, [target]);
  useEffect(load, [load]);

  return (
    <Sheet open={Boolean(target)} onOpenChange={(o) => !o && setTarget(null)}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="customer-card">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-zinc-200 bg-zinc-50/60 text-left space-y-3">
          <div>
            <SheetTitle className="font-heading text-2xl tracking-tight pr-8" data-testid="customer-card-name">{d ? d.name : d === false ? "Müşteri bulunamadı" : "Yükleniyor…"}</SheetTitle>
            <SheetDescription className="text-xs" data-testid="customer-card-meta">
              {d ? [d.tc_no && `TC ${d.tc_no}`, d.phone, d.birth_date && `Doğum ${formatDate(d.birth_date)}`].filter(Boolean).join(" · ") || "İletişim bilgisi yok" : ""}
            </SheetDescription>
          </div>
          {d && (
            <div className="flex flex-wrap gap-2">
              {d.phone
                ? <Button size="sm" variant="outline" asChild className="gap-1.5" data-testid="customer-card-call"><a href={`tel:${d.phone.replace(/[^\d+]/g, "")}`}><Phone className="h-4 w-4" />Ara</a></Button>
                : <Button size="sm" variant="outline" disabled className="gap-1.5" data-testid="customer-card-call"><Phone className="h-4 w-4" />Ara</Button>}
              <Button size="sm" disabled={!d.phone} onClick={() => openWhatsApp(d.phone, `Merhaba ${d.name},`)} className="gap-1.5 bg-[#25D366] hover:bg-[#1ebe5a] text-white" data-testid="customer-card-wa"><MessageCircle className="h-4 w-4" />WhatsApp</Button>
              {d.active_count > 0 && <Button size="sm" onClick={() => setWalletOpen(true)} className="gap-1.5 bg-amber-400 hover:bg-amber-300 text-[#03231a]" data-testid="customer-card-wallet"><WalletCards className="h-4 w-4" />Dijital Cüzdan Gönder</Button>}
              <Button size="sm" variant="ghost" onClick={() => { setTarget(null); navigate(`/customers?key=${encodeURIComponent(d.key)}`); }} className="gap-1.5 ml-auto" data-testid="customer-card-edit"><Pencil className="h-4 w-4" />Bilgileri düzenle</Button>
              {d.birthday_in_days != null && d.birthday_in_days <= 30 && <span className="inline-flex items-center gap-1 rounded-full bg-pink-50 border border-pink-200 px-2.5 text-xs text-pink-700"><Cake className="h-3.5 w-3.5" />{d.birthday_in_days === 0 ? "Doğum günü bugün" : `Doğum gününe ${d.birthday_in_days} gün`}</span>}
            </div>
          )}
        </SheetHeader>
        <div className="px-6 py-5 space-y-5">
          {d === null && <div className="space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>}
          {d && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="Aktif poliçe" value={`${d.active_count}${d.policy_count !== d.active_count ? ` / ${d.policy_count}` : ""}`} tid="customer-card-active" />
                <Stat label="Toplam prim" value={formatTRY(d.total_premium)} tid="customer-card-premium" />
                <Stat label="Net Komisyon" value={formatTRY(d.total_net_commission)} cls="text-emerald-700" tid="customer-card-commission" />
                <Stat label="Açık borç" value={formatTRY(d.open_debt)} cls={d.open_debt > 0 ? "text-rose-700" : "text-zinc-900"} tid="customer-card-debt" />
              </div>
              {d.overdue_days != null && (
                <div className="flex items-center gap-2" data-testid="customer-card-overdue">
                  <DebtAgeBadge days={d.overdue_days} />
                  <span className="text-xs text-zinc-500">en eski ödenmemiş borcun üzerinden geçen süre</span>
                </div>
              )}
              <Notes d={d} />
              <Opportunities list={d.opportunities || []} />
              <Vehicles list={d.vehicles} />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-3">Geçmiş · en yeni en üstte</p>
                <CustomerTimeline events={d.events} />
              </div>
            </>
          )}
        </div>
      </SheetContent>
      {d && <WalletDialog open={walletOpen} onOpenChange={setWalletOpen} customer={{ customer_key: d.key, name: d.name, phone: d.phone }} />}
    </Sheet>
  );
};
